import React, { useEffect, useState } from "react";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import Loader from "../../UI/Loader/Loader";
import { Form, Formik } from "formik";
import { FormikInput, FormikSelect, PrimaryButton, SecondaryButton } from "../../UI";
import { useUserContext } from "../../../contexts/UserContext";
import { updateAdobeTargetAudience, getAdobeTargetAudience } from "../../../utils/APIs";
import { UpdateAdobeTargetAudienceSchema } from "../../../schemas";
import { toast } from 'react-toastify';
import { useContentfulContext } from "../../../contexts/ContentfulContext";

const UpdateAdobeTargetAudienceModal = ({ setShowAdobeTargetAudience, audienceData, success, setSuccess, getTealiumAudienceStreamData }) => {
  const { userData } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const comparison_rules = [
    { name: "Equals", id: "equals" },
    {
      name: "Equals Ignore Case",
      id: "equalsIgnoreCase",
    },
    { name: "Contains", id: "contains" },
    { name: "Contains ignore case", id: "containsIgnoreCase" },
  ];

  function getComparisonRules(obj) {
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        return { key, value: obj[key] };
      }
    }
    return null;
  }

  const [existingConfiguredRule, setExistingConfiguredRule] = useState(null);

  const getAdobeTargetAudienceById = async () => {
    try {
      setLoading(true);
      const result = await getAdobeTargetAudience(
        userData.token,
        userData.selectedOrganization.org_token,
        { 
          tenant: userData.defaultAdobeIO?.tenant_name,
          id: audienceData.at_audience_id,
        }
      );
      setExistingConfiguredRule({
        mbox_parameter_name: result.data.targetRule.mbox,
        comparison_rule: getComparisonRules(result.data.targetRule).key,
        audience_id: getComparisonRules(result.data.targetRule).value
      });
      setError("");
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getAdobeTargetAudienceById();
  }, []);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

       await updateAdobeTargetAudience(
        userData.token,
        userData.selectedOrganization.org_token,
        audienceData._id,
        { 
          tenant: userData.defaultAdobeIO?.tenant_name,
          tealium_as_audience_id: audienceData?.tealium_as_audience_id, 
          at_audience_id: audienceData?.at_audience_id,
          at_audience_name: values?.at_audience_name, 
          at_audience_workspace: audienceData?.at_audience_workspace, 
          adobe_io_integration_id: audienceData?.adobe_io_integration_id, 
          adobe_target_audience_config_rule_id: audienceData?.adobe_target_audience_config_rule_id,
          targetRule: {
            [values?.comparison_rule]: existingConfiguredRule.audience_id,
            "mbox": values?.mbox_parameter_name
          }
        }
      );
      setError("");
      toast.success("Adobe Target Audience Updated Successfully");
      getTealiumAudienceStreamData();
      handleClose();
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowAdobeTargetAudience(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  return (
    <>
      <PrimaryModal
        title={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.title}
        hideModal={handleClose}
        widthClass="max-w-3xl"
      >
        {loading && <Loader />}

        <div className="p-7 pt-1 space-y-6">
          <Formik
            enableReinitialize={true}
            initialValues={{
              "at_audience_name": audienceData.at_audience_name,
              "at_audience_id": audienceData.at_audience_id,
              "as_audience_name": audienceData.name,
              "as_audience_id": audienceData.id,
              "mbox_parameter_name": existingConfiguredRule?.mbox_parameter_name || "",
              "comparison_rule": existingConfiguredRule?.comparison_rule || ""
            }}
            validationSchema={() => UpdateAdobeTargetAudienceSchema(content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT)}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form action="#" className="pr-3 space-y-4">
                <p>{content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.description}</p>
                <FormikInput
                  type="text"
                  id="at_audience_name"
                  name="at_audience_name"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.at_audience_name}
                />
                <FormikInput
                  type="text"
                  id="mbox_parameter_name"
                  name="mbox_parameter_name"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.mbox_parameter_name}
                />
                <FormikSelect
                  id="comparison_rule"
                  name="comparison_rule"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.comparison_rule}
                  helperText={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.comparison_rule_description}
                  options={comparison_rules.map(r => ({ value: r.id, label: r.name }))}
                  placeholder="Select comparison rule"
                />
                <FormikInput
                  type="text"
                  disabled
                  id="at_audience_id"
                  name="at_audience_id"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.at_audience_id}
                />
                <FormikInput
                  type="text"
                  disabled
                  id="as_audience_name"
                  name="as_audience_name"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.as_audience_name}
                />
                <FormikInput
                  type="text"
                  disabled
                  id="as_audience_id"
                  name="as_audience_id"
                  label={content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.formLabels.as_audience_id}
                />
                {error && <p className="mt-2 text-sm text-red-5">{error}</p>}
                <div className="flex flex-col md:flex-row justify-end gap-2 pt-2">
                  <div onClick={handleClose} className="w-full md:w-auto">
                    <SecondaryButton type="button" className="w-full">
                      {
                        content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.cancel_cta
                      }
                    </SecondaryButton>
                  </div>
                  <PrimaryButton type="submit" className="w-full md:w-auto">
                    {content.UPDATE_ADOBE_TARGET_AUDIENCE_MODAL_CONTENT.submit_cta}
                  </PrimaryButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </PrimaryModal>
    </>
  );
};

export default UpdateAdobeTargetAudienceModal;
